import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useSelector } from 'react-redux';
import ToggleSwitch from '../ToggleSwitch';
import GoatBadge from '../Tier/GoatBadge';
import BackupRestoreModal from '../BackupRestoreModal';
import { useTierGate } from '../../hooks/useTierGate';
import { track } from '../../utils/analytics';
import { forceLogout } from '../../redux/utils/refreshAndRetry';
import { useBackupToastHidden, restoreBackupToast } from '../../utils/backupToastVisibility';
import { latchBackupStop, clearBackupStopLatch, isBackupStopLatched } from '../../utils/backupStopLatch';
import {
    backupStorage,
    backupLayerRows,
    backupEnablePatch,
    backupPreflightSummary,
    backupPreviewAllStored,
    BACKUP_TOOLS, backupToolStatusLine, backupFilesStatusLine, backupFilesCountLine,
    trackSnapshotResults, formatBytes, formatTimeAgo, deriveBackupHeroState, middleEllipsis,
    summarizeCloudBackup,
    backupProgressLine, backupProgressPct, completionPct, backupStageLabel, backupInFlightLabel,
    BACKUP_SNOOZE_OPTIONS, backupSnoozeLine, BACKUP_SPEED_OPTIONS,
    backupSnapshotIssues, describeBackupError, joinNames,
} from '../../utils/cloudBackup';
import rekordboxGlyph from '../../assets/source-glyph-rekordbox.png';
import seratoGlyph from '../../assets/source-glyph-serato.png';
import traktorGlyph from '../../assets/source-glyph-traktor.png';
import virtualdjGlyph from '../../assets/source-glyph-virtualdj.png';
import './BackupSection.scss';

const TOOL_GLYPHS = {
    rekordbox: rekordboxGlyph,
    serato: seratoGlyph,
    traktor: traktorGlyph,
    virtualdj: virtualdjGlyph,
};

// How long a progress event stays "live" with no successor before the hero
// falls back to status-derived state (and refetches it).
const PROGRESS_STALE_MS = 6000;


/**
 * Dev QA hook, sibling of `window.__mockBackupProgress` in useBackupRun: a
 * shallow overlay on a real backup:getStatus result so states that otherwise
 * need real-world conditions can be driven on demand. The frozen state is the
 * motivating one — it needs an entitlement to actually lapse, which is not
 * something QA can arrange, and it is the state the whole retention design
 * hangs on. Overriding the preload bridge is not an option: contextBridge
 * objects are immutable.
 *
 *   window.__mockBackupStatus = { usage: { frozen: true, daysUntilPurge: 7 } }
 *   delete window.__mockBackupStatus   // back to the truth
 *
 * Never present in production builds.
 */
function applyStatusMock(status) {
    if (process.env.NODE_ENV === 'production') return status;
    const mock = typeof window !== 'undefined' ? window.__mockBackupStatus : null;
    if (!mock) return status;
    return {
        ...status,
        ...mock,
        settings: { ...status.settings, ...(mock.settings || {}) },
        files: { ...status.files, ...(mock.files || {}) },
        usage: { ...status.usage, ...(mock.usage || {}) },
    };
}
const CEREMONY_FIRST_MS = 6000;
const CEREMONY_AGAIN_MS = 3500;

/* Inline hero icons — 24px stroke glyphs on currentColor (no emoji). */
const iconProps = { width: 22, height: 22, viewBox: '0 0 24 24', fill: 'none', stroke: 'currentColor', strokeWidth: 2, strokeLinecap: 'round', strokeLinejoin: 'round' };
const ShieldSlashIcon = () => (
    <svg {...iconProps}><path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z" /><line x1="4" y1="4" x2="20" y2="20" /></svg>
);
const ShieldCheckIcon = () => (
    <svg {...iconProps}><path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z" /><polyline points="8.5 11.5 11 14 15.5 9" /></svg>
);
const CloudUpIcon = () => (
    <svg {...iconProps}><path d="M20 17.6A4.5 4.5 0 0 0 17.5 9.4a6 6 0 0 0-11.6 1.6A4 4 0 0 0 6 19h11" /><path d="M12 18v-6M9.5 14.5L12 12l2.5 2.5" /></svg>
);
const WarningIcon = () => (
    <svg {...iconProps}><path d="M10.29 3.86 1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z" /><line x1="12" y1="9" x2="12" y2="13" /><line x1="12" y1="17" x2="12.01" y2="17" /></svg>
);
// Ceremony check: pathLength lets CSS animate stroke-dashoffset 1 → 0.
const CheckDrawIcon = () => (
    <svg {...iconProps} className="bks-check-draw"><polyline points="4.5 12.5 9.5 17.5 19.5 6.5" pathLength="1" /></svg>
);
const SmallCheckIcon = () => (
    <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round"><polyline points="4.5 12.5 9.5 17.5 19.5 6.5" /></svg>
);

const PauseIcon = () => (
    <svg {...iconProps}><rect x="7" y="5" width="3.5" height="14" rx="1" /><rect x="13.5" y="5" width="3.5" height="14" rx="1" /></svg>
);

const ClockIcon = () => (
    <svg {...iconProps}><circle cx="12" cy="12" r="9" /><polyline points="12 7 12 12 15.5 14" /></svg>
);

const HERO_ICONS = {
    off: ShieldSlashIcon,
    paused: PauseIcon,
    snoozed: ClockIcon,
    waiting_network: ClockIcon,
    paused_entitlement: WarningIcon,
    frozen: WarningIcon,
    backing_up: CloudUpIcon,
    queued: CloudUpIcon,
    attention: WarningIcon,
    protected: ShieldCheckIcon,
};

/**
 * Cloud Backup section of the Settings page — the whole surface: status hero,
 * storage meter, per-app cards, config toggles, restore entry points, the
 * music-files pre-flight sheet and the failure-review list.
 *
 * Desktop only: renders nothing when the backup bridge is absent (web/native
 * shims don't expose it). Extracted from SettingsTab so the section can grow
 * without swallowing the page.
 */
function BackupSection({ standalone = false }) {
    const backupApi = typeof window !== 'undefined' ? window.electron?.backup : null;
    const isAuthenticated = useSelector((s) => !!s.auth?.user?.id);
    const userId = useSelector((s) => s.auth?.user?.id);
    const { canAccess: canBackup, isTrial, requireUpgrade: requireBackupUpgrade } = useTierGate('cloud-backup');

    const [backupStatus, setBackupStatus] = useState(null);
    const [backingUp, setBackingUp] = useState(false);
    const [snapshotResults, setSnapshotResults] = useState(null);
    const [showRestore, setShowRestore] = useState(false);
    const [restoreTool, setRestoreTool] = useState(null);

    const [backupProgress, setBackupProgress] = useState(null);
    const [filesProgress, setFilesProgress] = useState(null);
    // A files RESTORE in flight — its own stream and card. The wizard runs it
    // in the main process, so it outlives the wizard window; without this the
    // page showed nothing while gigabytes were downloading.
    const [restoreProgress, setRestoreProgress] = useState(null);
    const [restoreStopping, setRestoreStopping] = useState(false);
    // One per stream — see the progress handler for why sharing one broke the
    // DJ-data row.
    const filesStaleTimer = useRef(null);
    const snapshotStaleTimer = useRef(null);
    const restoreStaleTimer = useRef(null);

    // 'first' = the one-time "Your DJ life is safe now." moment; 'again' = the
    // smaller check after later runs with uploads.
    const [ceremony, setCeremony] = useState(null);
    const prevFilesRunAtRef = useRef(undefined);
    const ceremonyDismissRef = useRef(null);

    const [issuesOpen, setIssuesOpen] = useState(false);
    const [issues, setIssues] = useState(null);
    const [retrying, setRetrying] = useState(false);

    // null = closed; { loading } or { count, bytes } = sheet visible.
    const [preflight, setPreflight] = useState(null);

    // What the account already holds in the cloud — probed once per owner,
    // only while backup is off here, so the off-state hero can say "off on
    // THIS computer, your cloud copy is safe" instead of the account-wide lie
    // "nothing is backed up". Three states: undefined = probe not resolved
    // yet (the hero shows ghost lines, never a flash of "Not protected" that
    // a second later corrects itself and pushes the layout), null = probed
    // and the cloud is empty, object = the facts.
    const [cloudFacts, setCloudFacts] = useState(undefined);
    const cloudProbeRef = useRef(null); // owner id already probed this session

    // Hooks must all run before the `if (!backupApi) return null` below.
    const toastHidden = useBackupToastHidden();
    const [stopping, setStopping] = useState(false);
    const [stopMenuOpen, setStopMenuOpen] = useState(false);
    const [deleteOpen, setDeleteOpen] = useState(false);
    const [deleteConfirmText, setDeleteConfirmText] = useState('');
    const [deleting, setDeleting] = useState(false);
    const [deleteError, setDeleteError] = useState(null);
    // Set once a purge is accepted; cleared when the user starts anything else.
    const [deleteResult, setDeleteResult] = useState(null);

    // The snooze menu is a transient popover: any click elsewhere, or Escape,
    // dismisses it. Without this it survives navigation within the pane.
    useEffect(() => {
        if (!stopMenuOpen) return undefined;
        const close = (e) => { if (!e.target.closest?.('.bks-stop-wrap')) setStopMenuOpen(false); };
        const onKey = (e) => { if (e.key === 'Escape') setStopMenuOpen(false); };
        document.addEventListener('mousedown', close);
        document.addEventListener('keydown', onKey);
        return () => {
            document.removeEventListener('mousedown', close);
            document.removeEventListener('keydown', onKey);
        };
    }, [stopMenuOpen]);

    const refreshBackupStatus = useCallback(() => {
        if (!backupApi) return;
        backupApi.getStatus().then((s) => {
            if (!s || s.error) return;
            setBackupStatus(applyStatusMock(s));
        }).catch(() => {});
    }, [backupApi]);
    useEffect(() => { refreshBackupStatus(); }, [refreshBackupStatus, isAuthenticated]);
    // The Tabs layout keeps this pane mounted after the first visit, so the
    // status would otherwise freeze at its first fetch — refresh whenever the
    // user comes back to Settings, and poll lightly while they're looking.
    const activeTab = useSelector((s) => s.app?.activeTab);
    const trialDaysRemaining = useSelector((s) => s.tier?.trialDaysRemaining);
    const ownTab = standalone ? 'backup' : 'settings';
    // Refresh immediately when the user arrives on this pane...
    useEffect(() => {
        if (activeTab !== ownTab) return undefined;
        refreshBackupStatus();
        return undefined;
    }, [activeTab, ownTab, refreshBackupStatus]);
    // ...and keep polling while MOUNTED, not only while this pane is the
    // active tab. The engine's state changes behind the UI's back — a snooze
    // expires, an entitlement lapses, the other copy of this panel (Settings
    // and the Backup page both render it) stops the engine. Gating the poll on
    // the active tab let the hero sit on a minute-old status and offer an
    // action for a state the engine had already left: it showed "Back up now"
    // for a queue the user had actually STOPPED, and pressing it duly cleared
    // their own Stop. The UI must not offer actions based on state it hasn't
    // checked.
    useEffect(() => {
        const t = setInterval(refreshBackupStatus, 30000);
        const onFocus = () => refreshBackupStatus();
        window.addEventListener('focus', onFocus);
        return () => { clearInterval(t); window.removeEventListener('focus', onFocus); };
    }, [refreshBackupStatus]);

    // Probe the cloud once per owner while backup is off here. Gated on the
    // status saying signedIn — the renderer's auth can restore before the main
    // process's owner context does, and a probe fired into that gap comes back
    // empty and would pin the hero on "Not protected" for an account whose
    // backup is fine (same trap RestoreFoundPrompt hit).
    useEffect(() => {
        if (!backupApi || !isAuthenticated || !userId) return undefined;
        if (!backupStatus?.signedIn || backupStatus?.settings?.enabled) return undefined;
        if (cloudProbeRef.current === userId) return undefined;
        cloudProbeRef.current = userId;
        let cancelled = false;
        backupApi.restorePreview?.()?.then?.((p) => {
            if (cancelled) return;
            const facts = summarizeCloudBackup(p);
            setCloudFacts(facts);
            if (facts) {
                track('backup_off_cloud_notice_shown', {
                    tool_count: facts.toolCount,
                    files_total: facts.filesTotal,
                    hostname_known: !!facts.hostname,
                });
            }
        }).catch?.(() => {
            if (cancelled) return;
            cloudProbeRef.current = null;
            setCloudFacts(null); // probe failed — settle the hero on the plain off copy
        });
        return () => { cancelled = true; };
    }, [backupApi, isAuthenticated, userId, backupStatus?.signedIn, backupStatus?.settings?.enabled]);

    useEffect(() => {
        if (!backupApi?.onProgress) return undefined;
        return backupApi.onProgress((p) => {
            // The pass envelope (start/end of a whole backup) is the app-wide
            // toast's business; the hero reads the steps themselves.
            if (p?.phase === 'pass') return;
            // Files-engine events flow whenever it runs in the background;
            // snapshot events also arrive from post-auto-sync passes. Either
            // way the hero goes live, and a stale-timer settles it back to
            // status truth if the stream stops without a terminal event.
            if (p?.phase === 'files-restore') {
                // Only the engine's explicit `final` marker settles the card.
                // currentFile can be null MID-RUN (a lull between picks, the
                // checking pass) — treating that as terminal made the card
                // fall back to stale polled progress and the bar jump
                // backwards. The stale timer covers streams that die without
                // a final event (crash, main-process restart): confirm with
                // getStatus before clearing, same rule as the backup streams.
                const terminal = p.final === true;
                setRestoreProgress(terminal ? null : p);
                if (terminal) { setRestoreStopping(false); refreshBackupStatus(); }
                if (restoreStaleTimer.current) clearTimeout(restoreStaleTimer.current);
                if (!terminal) {
                    restoreStaleTimer.current = setTimeout(async () => {
                        const s = await backupApi.getStatus?.().catch(() => null);
                        if (s && !s.error) {
                            setBackupStatus(applyStatusMock(s));
                            if (s.restore?.active) return; // quiet, not over
                        }
                        setRestoreProgress(null);
                        setRestoreStopping(false);
                    }, PROGRESS_STALE_MS);
                }
                return;
            }
            if (p?.phase === 'files') {
                // A terminal event ('done' / 'stopped') settles the hero back
                // to status truth; everything else — including the count-only
                // event that opens a run — stays live so "X of Y" is on screen
                // from the first second, not only once bytes start moving.
                const terminal = p.stage === 'done' || p.stage === 'stopped';
                // Once Stop is pressed the engine keeps emitting for the files
                // already in flight — it takes a moment to wind down. Rendering
                // those made the hero flick back to "Uploading <next file>",
                // then to paused, then settle: three states for one click. The
                // user's decision wins immediately; only the terminal event
                // clears the latch.
                // See useBackupRun: the latch survives the terminal event and
                // is only lifted by an explicit user action, so one Stop
                // produces exactly one visual transition.
                if (isBackupStopLatched()) { setFilesProgress(null); if (terminal) refreshBackupStatus(); return; }
                setFilesProgress(terminal ? null : p);
                if (terminal) refreshBackupStatus();
            } else {
                // 'done' ends a snapshot pass — settle straight away rather
                // than waiting for the stale timer to notice the silence.
                setBackupProgress(p.phase === 'done' ? null : p);
                if (p.phase === 'done') refreshBackupStatus();
            }
            // Silence is not proof the run ended — the engine goes quiet for a
            // whole negotiate round-trip. Confirm with the engine before
            // clearing, or a healthy run's quiet moment collapses the hero
            // back to "stopped" and then straight into progress again.
            //
            // Files and snapshots are SEPARATE streams with separate lifetimes,
            // and they need separate timers. Sharing one meant the "files are
            // still running, leave it alone" guard also skipped clearing the
            // snapshot progress — so once a files run was under way (hours),
            // the DJ-data row stayed on "Backing up…" permanently, next to tool
            // cards that correctly said "Backed up 23m ago".
            if (p?.phase === 'files') {
                if (filesStaleTimer.current) clearTimeout(filesStaleTimer.current);
                filesStaleTimer.current = setTimeout(async () => {
                    const s = await backupApi.getStatus?.().catch(() => null);
                    if (s && !s.error) {
                        setBackupStatus(applyStatusMock(s));
                        if (s.files?.state === 'running') return; // quiet, not over
                    }
                    setFilesProgress(null);
                }, PROGRESS_STALE_MS);
            } else {
                if (snapshotStaleTimer.current) clearTimeout(snapshotStaleTimer.current);
                snapshotStaleTimer.current = setTimeout(async () => {
                    const s = await backupApi.getStatus?.().catch(() => null);
                    if (s && !s.error) {
                        setBackupStatus(applyStatusMock(s));
                        if (s.snapshotRunning) return; // quiet, not over
                    }
                    setBackupProgress(null);
                }, PROGRESS_STALE_MS);
            }
        });
    }, [backupApi, refreshBackupStatus]);
    useEffect(() => () => {
        if (filesStaleTimer.current) clearTimeout(filesStaleTimer.current);
        if (snapshotStaleTimer.current) clearTimeout(snapshotStaleTimer.current);
        if (restoreStaleTimer.current) clearTimeout(restoreStaleTimer.current);
    }, []);

    // The freeze is the conversion moment — measure that it was actually seen,
    // not just that the sweep stamped a lapse server-side.
    const frozenSeenRef = useRef(false);
    useEffect(() => {
        const frozen = !!backupStatus?.usage?.frozen;
        if (frozen && !frozenSeenRef.current) {
            frozenSeenRef.current = true;
            track('backup_frozen_shown', {
                days_until_purge: backupStatus?.usage?.daysUntilPurge ?? null,
                file_count: backupStatus?.usage?.fileCount ?? 0,
            });
        } else if (!frozen) {
            frozenSeenRef.current = false;
        }
    }, [backupStatus]);

    // First-success ceremony: fires when a files run lands with uploads. The
    // ref seeds from the FIRST status we see — `null` there means this user
    // had never completed a files run, so the next completion is the big one.
    useEffect(() => {
        if (!backupStatus) return undefined;
        const run = backupStatus.settings?.lastFilesRun || null;
        const prev = prevFilesRunAtRef.current;
        if (prev === undefined) {
            prevFilesRunAtRef.current = run ? run.at : null;
            return undefined;
        }
        if (!run || run.at === prev) return undefined;
        prevFilesRunAtRef.current = run.at;
        if (run.status !== 'completed' || (run.uploadedCount || 0) === 0) return undefined;
        const kind = prev === null ? 'first' : 'again';
        setCeremony(kind);
        // The dismiss timer lives in a ref, NOT in this effect's cleanup: the
        // effect re-runs on every status change (30s poll, focus, progress
        // refreshes — constant while a run is live), and a cleanup-held timer
        // was cancelled by the first refresh inside the ceremony window while
        // the early returns above never rescheduled it. The "safe now" hero
        // then stayed up for the rest of the session — suppressing Stop, live
        // progress and the toast-restore link, all gated on !ceremony.
        clearTimeout(ceremonyDismissRef.current);
        ceremonyDismissRef.current = setTimeout(() => setCeremony(null), kind === 'first' ? CEREMONY_FIRST_MS : CEREMONY_AGAIN_MS);
        return undefined;
    }, [backupStatus]);
    useEffect(() => () => clearTimeout(ceremonyDismissRef.current), []);

    const settings = backupStatus?.settings;
    const usage = backupStatus?.usage;

    const applyBackupPatch = async (patch, enabledLayer) => {
        if (!backupApi) return;
        // Turning a layer on (or the master switch) starts a run; the latch
        // from an earlier Stop must not silence it.
        clearBackupStopLatch();
        if (enabledLayer && !canBackup) { requireBackupUpgrade(); return; }
        const res = await backupApi.setEnabled(patch);
        if (res?.error === 'UPGRADE_REQUIRED') { requireBackupUpgrade(); return; }
        if (res?.settings) {
            setBackupStatus((s) => (s ? { ...s, settings: res.settings } : s));
            if (enabledLayer === 'files') track('backup_files_enabled');
            else if (enabledLayer) track('backup_enabled', { layer: enabledLayer });
            refreshBackupStatus();
        }
    };

    // The snapshot half of "Check now" on its own — also what "Retry all" in
    // the failure review runs for its DJ-data rows. `from` tags the per-tool
    // analytics. Returns false when the pass was handed off to a sheet (the
    // upgrade prompt, the shrink guard) so the caller stops there.
    const runSnapshotPass = async (from, tools, extra = {}) => {
        const res = await backupApi.runSnapshot({ force: true, ...(tools ? { tools } : {}), ...extra });
        if (res?.error === 'UPGRADE_REQUIRED') { requireBackupUpgrade(); return false; }
        // First backup on a machine that looks emptier than the cloud's
        // newest snapshot: nothing was uploaded — hand the decision to the
        // ShrinkGuardPrompt sheet (App-mounted) and stop here. It re-runs
        // with allowShrink if the user says "back up anyway".
        const guard = res?.results?.find((r) => r.status === 'needs_confirmation');
        if (guard) {
            window.dispatchEvent(new CustomEvent('mixgoat:backup-shrink-guard', { detail: guard.guard }));
            return false;
        }
        if (res?.results) {
            setSnapshotResults(res.results);
            trackSnapshotResults(res.results, from);
        }
        return true;
    };

    const backUpNow = async () => {
        if (!backupApi || backingUp) return;
        if (!canBackup) { requireBackupUpgrade(); return; }
        setBackingUp(true);
        clearBackupStopLatch();
        setSnapshotResults(null);
        try {
            // DJ data FIRST, files second, and deliberately in that order.
            // The snapshot is seconds and megabytes; the files pass is hours
            // and gigabytes. Running them together (which is what firing both
            // at once did) meant the fast, most-irreplaceable layer — cues,
            // grids, playlists — sat behind the slow one for no reason, and
            // the user had nothing finished to look at for the first hour.
            // Snapshot first gives them a green tick almost immediately.
            //
            // Both legs run in the main process as ONE backup pass: it
            // continues into the files leg the moment the snapshots are
            // through (`thenFiles`), so there is no second IPC from here and
            // no gap for the app-wide toast to drop into. It reads the live
            // files state itself — the `backupStatus` cache is refreshed on
            // a tick, so a Stop taken seconds ago still looks like 'idle' —
            // and leaves a stopped or snoozed files leg alone: this is a
            // check, not a Resume. A stopped backup that quietly un-stops
            // itself is the worst kind of bug in a feature whose entire
            // promise is that it does what you told it.
            await runSnapshotPass('settings', undefined, { thenFiles: true });
        } finally {
            setBackingUp(false);
            setBackupProgress(null);
            refreshBackupStatus();
        }
    };

    // ── Turning something ON goes through the pre-flight sheet: real numbers
    // before real gigabytes move.
    //
    // Two modes. 'files' is the Music-files sub-toggle. 'enable' is the master
    // switch, and it ASKS which layers rather than silently applying a default
    // — DJ app data ships off, and it is the part that cannot be re-acquired:
    // cues, grids and playlists are hundreds of hours of work, against audio
    // files you could download again. Quietly backing up the replaceable half
    // and skipping the irreplaceable one is not a default worth applying on
    // someone's behalf. Both start ticked; either can be unticked. ──
    const openPreflight = async (mode) => {
        if (!canBackup) { requireBackupUpgrade(); return; }
        setPreflight({
            loading: true,
            mode,
            pickFiles: true,
            // Only pre-tick DJ data if there is a DJ app to snapshot.
            pickDj: mode === 'enable' && BACKUP_TOOLS.some(({ tool }) => backupStatus?.detected?.[tool]),
            // Per-app picks: absent = ticked, so every detected app starts
            // ticked. The visible choice is the point — the protect-everything
            // default is not up for silent change.
            pickTools: {},
        });
        track('backup_preflight_shown', { mode });
        try {
            const p = await backupApi.filesPreview();
            setPreflight((cur) => (cur ? { ...cur, loading: false, count: p?.count || 0, bytes: p?.bytes || 0, sized: p?.sized ?? 0, stored: p?.stored || 0 } : cur));
        } catch {
            setPreflight((cur) => (cur ? { ...cur, loading: false, count: 0, bytes: 0, stored: 0 } : cur));
        }
    };
    const onFilesToggle = async () => {
        if (settings?.files === true) {
            applyBackupPatch({ files: false }, null);
            return;
        }
        await openPreflight('files');
    };
    const onMasterToggle = async () => {
        if (settings?.enabled) { applyBackupPatch({ enabled: false }, null); return; }
        await openPreflight('enable');
    };
    const confirmPreflight = () => {
        const p = preflight;
        setPreflight(null);
        // The sheet's promise IS the patch: backupEnablePatch names every
        // detected tool explicitly from the per-app ticks, so what was shown
        // ticked is exactly what gets backed up — no persisted per-tool
        // switch can silently narrow (or widen) an explicit opt-in.
        const enable = p?.mode === 'enable' ? backupEnablePatch(p, detectedTools) : null;
        track('backup_preflight_confirmed', {
            mode: p?.mode || 'files',
            files: p?.mode === 'enable' ? !!p.pickFiles : true,
            dj_data: enable ? enable.patch.djData : undefined,
            dj_apps_selected: enable ? enable.djAppsSelected : undefined,
            dj_apps_detected: enable ? detectedTools.length : undefined,
            files_already_stored: backupPreviewAllStored(p),
        });
        if (enable) {
            applyBackupPatch(enable.patch, p.pickFiles ? 'files' : 'dj_data');
            return;
        }
        applyBackupPatch({ files: true }, 'files');
    };
    const dismissPreflight = () => {
        setPreflight(null);
        track('backup_preflight_dismissed');
    };

    // ── Failure review ──
    const loadIssues = useCallback(() => {
        backupApi?.filesIssues().then((res) => {
            if (res && !res.error) setIssues(res);
        }).catch(() => {});
    }, [backupApi]);
    const toggleIssues = () => {
        setIssuesOpen((open) => {
            if (!open) loadIssues();
            return !open;
        });
    };
    const retryAll = async () => {
        if (retrying || !backupApi) return;
        setRetrying(true);
        try {
            // Two kinds of row, two different retries. Music files are queue
            // rows: requeue them and kick the engine. DJ-data rows are the
            // last snapshot result per app, and the only retry is another
            // snapshot pass. Until this ran both, "Retry all" under a
            // "Rekordbox backup failed" hero requeued zero files, reported
            // nothing, and left the failure exactly where it was.
            const snapIssues = backupSnapshotIssues(backupStatus);
            if (snapIssues.length) {
                if (!canBackup) { requireBackupUpgrade(); return; }
                track('backup_snapshot_retry', { tools: snapIssues.length, from: 'issues' });
                setBackingUp(true);
                clearBackupStopLatch();
                setSnapshotResults(null);
                try {
                    // Only the apps that failed: a retry is not a full
                    // re-check, and re-hashing an app that is already up to
                    // date puts "Checking Rekordbox" on screen for a Serato
                    // problem.
                    await runSnapshotPass('issues', snapIssues.map((s) => s.tool));
                } finally {
                    setBackingUp(false);
                    setBackupProgress(null);
                }
            }
            if ((issues?.failed?.length || 0) + (issues?.skippedQuota?.length || 0) > 0) {
                const res = await backupApi.retryFiles();
                track('backup_files_retry', { count: res?.retried || 0 });
            }
            setIssuesOpen(false);
            setIssues(null);
            refreshBackupStatus();
        } finally {
            setRetrying(false);
        }
    };

    // ── Stop / Resume ──
    // Stop is durable (persisted per owner in the main process): a restart
    // must not quietly resume a backup the user stopped. Every part etag
    // already collected survives, so Resume continues rather than restarts.
    const stopFiles = async () => {
        // Latched = a stop is already winding down; a second click (keyboard,
        // or a race before the disabled state paints) must not re-fire.
        if (!backupApi || stopping || isBackupStopLatched()) return;
        latchBackupStop();
        setFilesProgress(null);
        setStopping(true);
        try {
            await backupApi.pauseFiles();
            track('backup_files_stopped', { from: 'hero' });
            setFilesProgress(null);
            refreshBackupStatus();
        } finally {
            setStopping(false);
        }
    };

    const resumeFiles = async () => {
        if (!backupApi) return;
        clearBackupStopLatch();
        await backupApi.resumeFiles();
        track('backup_files_resumed', { from: 'hero' });
        refreshBackupStatus();
    };

    // Snooze: Stop's "…for a while" sibling. Stop stays exactly as it was —
    // indefinite, deliberate — and these presets sit behind the same control
    // for the far commoner "not right now, I'm working" case.
    const snoozeFiles = async (minutes) => {
        if (!backupApi?.snoozeFiles) return;
        setStopMenuOpen(false);
        latchBackupStop();
        setStopping(true);
        try {
            await backupApi.snoozeFiles(minutes);
            track('backup_files_snoozed', { minutes, from: 'hero' });
            setFilesProgress(null);
            refreshBackupStatus();
        } finally {
            setStopping(false);
        }
    };

    // ── Delete everything ──
    // Typed confirmation, then one server call that wipes files + snapshots +
    // usage rows, and a local reset (queue + change-gate hashes) so the app
    // doesn't sit there believing the cloud copy still exists.
    const confirmDelete = async () => {
        if (!backupApi || deleting) return;
        setDeleting(true);
        setDeleteError(null);
        setDeleteResult(null);
        try {
            const res = await backupApi.purge();
            if (res?.error === 'NOT_AUTHENTICATED') {
                // The session is gone (refresh token rejected): nothing in the
                // app can succeed until the user signs in again, so hand over
                // to the same sign-in flow every other call uses. No copy
                // telling the user to sign out and back in themselves.
                setDeleteOpen(false);
                setDeleteConfirmText('');
                forceLogout();
                return;
            }
            if (res?.error === 'AUTH_RATE_LIMITED') {
                // The sign-in server refused to renew the token for a while
                // (Retry-After). Transient: say when, keep the modal open.
                const secs = Math.max(1, Math.ceil((res.retryAfterMs || 60000) / 1000));
                setDeleteError(secs > 90
                    ? `Couldn\u2019t renew your sign-in just now. Try again in about ${Math.ceil(secs / 60)} minutes.`
                    : `Couldn\u2019t renew your sign-in just now. Try again in ${secs}s.`);
                return;
            }
            if (res?.error === 'AUTH_TRANSIENT') {
                setDeleteError('Couldn\u2019t reach the sign-in server. Check your connection and try again.');
                return;
            }
            if (res?.error) {
                setDeleteError(res.error);
                return;
            }
            track('backup_deleted', { from: 'settings' });
            setDeleteOpen(false);
            setDeleteConfirmText('');
            setIssues(null);
            setIssuesOpen(false);
            setSnapshotResults(null);
            // Say what was removed, and say that the removal itself runs on the
            // server. The modal used to just close: after typing DELETE and
            // handing over tens of gigabytes, the entire feedback was a panel
            // that quietly went blank. Counts are captured BEFORE the purge
            // because the usage row is one of the things it deletes.
            setDeleteResult({
                files: usage?.fileCount ?? 0,
                snapshots: BACKUP_TOOLS.filter(({ tool }) => settings?.lastSnapshot?.[tool]).length,
            });
            refreshBackupStatus();
        } catch (err) {
            setDeleteError(err?.message || 'Delete failed');
        } finally {
            setDeleting(false);
        }
    };

    const openRestoreFor = (tool) => { setRestoreTool(tool); setShowRestore(true); };

    // ── Stop a running restore ──
    // Fire-and-forget: the engine stops between files (the one in flight
    // finishes and verifies), and the wizard's running call returns with
    // `stopped: true`. Restore is hash-resumable, so this is pause, not loss.
    const stopRestore = () => {
        if (!backupApi?.restoreStop || restoreStopping) return;
        setRestoreStopping(true);
        track('backup_restore_stopped', { from: 'backup_page' });
        backupApi.restoreStop().catch(() => {});
    };

    if (!backupApi) return null;

    // ── Hero derivation: live progress beats the (possibly minute-old)
    // status; the ceremony beats everything for its few seconds. ──
    const statusHeroState = deriveBackupHeroState(backupStatus);
    const liveBackingUp = !!filesProgress
        || (backupProgress && (backupProgress.phase === 'building' || backupProgress.phase === 'uploading'));
    const heroState = statusHeroState === 'off' ? 'off'
        // Frozen and entitlement-paused both outrank live progress: whatever
        // the engine thinks it is doing, the server is refusing the uploads.
        : statusHeroState === 'frozen' ? 'frozen'
        : statusHeroState === 'paused_entitlement' ? 'paused_entitlement'
        : liveBackingUp ? 'backing_up'
        : statusHeroState;

    // The Stop latch outlives the click (it survives until an explicit
    // resume/enable), so every stop-aware surface — hero title, layer rows,
    // and the Stop button itself — reads this, not the transient `stopping`
    // flag (which only spans the pauseFiles IPC call and re-armed the button
    // while the hero still said "Stopping…").
    const stopPending = stopping || isBackupStopLatched();

    const hero = buildHero({
        heroState, ceremony, backupStatus, settings, usage,
        filesProgress, backupProgress, isAuthenticated, cloudFacts,
        stopRequested: stopPending,
    });
    const HeroIcon = ceremony ? CheckDrawIcon : HERO_ICONS[heroState];
    const heroTone = ceremony ? 'success'
        : heroState === 'frozen' ? 'warning'
        : heroState === 'off' ? 'muted'
        : heroState === 'paused' ? 'muted'
        // Snooze and metered-hold are things the user asked for (or configured),
        // not problems — muted, never the warning tone.
        : heroState === 'snoozed' ? 'muted'
        : heroState === 'waiting_network' ? 'muted'
        : heroState === 'protected' ? 'success'
        : (heroState === 'backing_up' || heroState === 'queued') ? 'accent'
        : 'warning';

    // With backup off, the master toggle and "Back up now" are duplicates of
    // the hero's own CTA (same applyBackupPatch call) — the page offered the
    // same action three times. Off = hero only; the toggle returns the moment
    // backup is on, where it IS the master switch and parents the sub-toggles.
    const backupOff = !settings?.enabled;

    // A restore in flight — started from this window's wizard or not (the run
    // lives in the main process and outlives the window that started it).
    // Live events beat the polled status; the status covers a mount mid-run.
    const liveRestore = restoreProgress || backupStatus?.restore?.progress || null;
    const restoreActive = !!restoreProgress || !!backupStatus?.restore?.active;

    // Frozen / entitlement-paused suppress the Stop control. A local run can
    // still be in flight in those states (the engine finds out it is blocked
    // only when the server refuses a batch), and offering "Stop" there puts
    // the wrong verb on the screen: nothing is being backed up to stop, and
    // the action that matters — Renew — is the hero's own CTA, which the Stop
    // block would otherwise replace.
    const uploadsBlocked = heroState === 'frozen' || heroState === 'paused_entitlement';
    const filesRunning = !uploadsBlocked
        && (!!filesProgress || backupStatus?.files?.state === 'running');
    // Stop shows for the WHOLE backing_up state, snapshot phase included. It
    // used to be files-engine-only ("a snapshot pass is seconds long and has
    // no stop path"), which left an enable-triggered run with no way out
    // while the hero said "Preparing…" — exactly when someone who clicked by
    // accident is looking for one. Stop pauses the files leg that follows;
    // the snapshot pass finishes on its own and the hero lands on "Backup
    // stopped".
    const runStoppable = filesRunning || (!uploadsBlocked && heroState === 'backing_up');

    const issueCounts = backupStatus?.files?.counts;
    const failedIssues = issues?.failed || [];
    const quotaIssues = issues?.skippedQuota || [];
    const snapshotIssues = backupSnapshotIssues(backupStatus);

    const storage = backupStorage(usage);
    // Which DJ apps this machine actually has — drives the enable sheet, so it
    // names what it will back up instead of offering the layer blind.
    const detectedTools = BACKUP_TOOLS.filter(({ tool }) => backupStatus?.detected?.[tool]);
    const layerRows = backupLayerRows(backupStatus, {
        filesRunning: !!filesProgress,
        snapshotRunning: backupProgress ? true : undefined,
        snapshotUploading: backupProgress?.phase === 'uploading',
        stopRequested: stopPending,
        libraryDone: filesProgress?.libraryDone,
        libraryTotal: filesProgress?.libraryTotal,
    });
    // Coverage, not bytes, is the headline: "how many of my tracks are safe" is
    // the question a DJ actually has, and it is the only one that stays
    // meaningful across a 200-track laptop library and a 60 GB archive.
    // Same live-beats-polled rule as the layer rows: the pair comes from ONE
    // source, never live stored over a polled total.
    const coverage = (() => {
        if (filesProgress?.libraryTotal > 0) {
            const total = filesProgress.libraryTotal;
            const stored = Math.min(filesProgress.libraryDone || 0, total);
            return { stored, total, pct: completionPct(stored, total) };
        }
        const c = backupStatus?.files?.counts;
        if (!c) return null;
        const total = Object.values(c).reduce((a, b) => a + (b || 0), 0);
        return total ? { stored: c.stored || 0, total, pct: completionPct(c.stored, total) } : null;
    })();

    return (
        <div className={`bks-section${standalone ? ' bks-section--standalone' : ''}`}>
            {!standalone && (
                <div className="bks-section-label">
                    Cloud Backup
                    {!canBackup && <GoatBadge />}
                </div>
            )}

            {/* ── (1) Status hero + (4) what's protected + (2) storage ── */}
            <div className="bks-card">
                <div className={`bks-hero bks-hero--${heroTone}`}>
                    <div className={`bks-hero-icon${heroState === 'backing_up' && !ceremony ? ' bks-hero-icon--pulse' : ''}`}>
                        <HeroIcon />
                    </div>
                    {/* Keyed on the state so React remounts the block and the
                        fade replays: Stop and Resume swap every line at once,
                        and an instant swap of title + sub + CTA read as a jump
                        rather than a response. Live progress within one state
                        must NOT re-key, or it would fade on every file. */}
                    <div className="bks-hero-text" key={ceremony ? 'ceremony' : heroState}>
                        {/* Off-state, cloud probe still in flight: ghost the
                            two text lines instead of flashing "Not protected"
                            and then swapping to "your cloud copy is safe" —
                            the correction pushed the whole card. The CTA and
                            Restore link are identical in both outcomes, so
                            they render immediately. */}
                        {heroState === 'off' && isAuthenticated && cloudFacts === undefined && !ceremony ? (
                            <>
                                <div className="bks-hero-skel bks-hero-skel--title" />
                                <div className="bks-hero-skel bks-hero-skel--sub" />
                            </>
                        ) : (
                            <>
                        <div className="bks-hero-title">{hero.title}</div>
                        <div className="bks-hero-sub">
                            {hero.sub}
                            {heroState === 'attention' && !ceremony && (
                                <>
                                    {' '}
                                    <button type="button" className="bks-link" onClick={toggleIssues}>
                                        {issuesOpen ? 'Hide' : 'Review'}
                                    </button>
                                </>
                            )}
                        </div>
                            </>
                        )}
                        {/* Backup off = one primary action (the CTA). Restore
                            still has to be reachable — a fresh machine has
                            backup off and a cloud copy waiting — so it rides
                            here as a quiet secondary rather than competing in
                            a card of its own. */}
                        {backupOff && !ceremony && (
                            <div className="bks-hero-secondary">
                                Already have a backup?
                                {' '}
                                <button
                                    type="button"
                                    className="bks-link"
                                    onClick={() => { setRestoreTool(null); setShowRestore(true); }}
                                    disabled={!isAuthenticated}
                                >
                                    {restoreActive ? 'Restore in progress…' : 'Restore…'}
                                </button>
                            </div>
                        )}
                        {/* The files actually moving right now, oldest first.
                            One featured name made the parallelism invisible and
                            made a name change look like a completion; the list
                            shows what is really happening. */}
                        {hero.inFlightFiles?.length > 0 && (
                            <ul className="bks-hero-files">
                                {hero.inFlightFiles.map((f) => (
                                    <li key={f.name} className="bks-hero-file" title={f.name}>
                                        <span className="bks-hero-file-dot" />
                                        <span className="bks-hero-file-name">{f.name}</span>
                                        {f.attempt > 1 && (
                                            <span className="bks-hero-file-retry">{`retry ${f.attempt}`}</span>
                                        )}
                                    </li>
                                ))}
                            </ul>
                        )}
                        {/* Rendered only when there IS progress. It was briefly
                            reserved across every run-ish state to stop Stop and
                            Resume nudging the layout — but once the in-flight
                            file list arrived the states differed by far more
                            than one row, so all the reservation bought was a
                            hole under the sub-line in every paused and queued
                            state. The crossfade on state change covers the
                            movement; empty space does not. */}
                        {hero.pct != null && (
                            <div className="bks-hero-progress">
                                <div className="bks-progress-track">
                                    <div className="bks-progress-fill" style={{ width: `${hero.pct}%` }} />
                                </div>
                                <span className="bks-progress-pct">{hero.pct}%</span>
                            </div>
                        )}
                    </div>
                    {runStoppable && !ceremony && (
                        <div className="bks-stop-wrap">
                            <button
                                type="button"
                                className="settings-btn bks-hero-cta bks-stop-main"
                                onClick={stopFiles}
                                disabled={stopPending}
                            >
                                {stopPending ? 'Stopping…' : 'Stop'}
                            </button>
                            <button
                                type="button"
                                className="settings-btn bks-hero-cta bks-stop-caret"
                                aria-label="Pause for a while"
                                aria-expanded={stopMenuOpen}
                                disabled={stopPending}
                                onClick={() => setStopMenuOpen((v) => !v)}
                            >
                                <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><polyline points="6 9 12 15 18 9" /></svg>
                            </button>
                            {stopMenuOpen && (
                                <div className="bks-stop-menu" role="menu">
                                    {BACKUP_SNOOZE_OPTIONS.map((o) => (
                                        <button
                                            key={o.minutes}
                                            type="button"
                                            role="menuitem"
                                            className="bks-stop-menu-item"
                                            onClick={() => snoozeFiles(o.minutes)}
                                        >
                                            {o.label}
                                        </button>
                                    ))}
                                </div>
                            )}
                        </div>
                    )}
                    {hero.cta && (
                        <button
                            type="button"
                            className="settings-btn settings-btn--primary bks-hero-cta"
                            disabled={hero.cta.disabled}
                            onClick={hero.cta.onClick === 'enable'
                                ? () => openPreflight('enable')
                                : hero.cta.onClick === 'resume'
                                    ? resumeFiles
                                    : () => {
                                        if (heroState === 'frozen') {
                                            track('backup_frozen_upgrade_clicked', {
                                                days_until_purge: usage?.daysUntilPurge ?? null,
                                            });
                                        }
                                        requireBackupUpgrade();
                                    }}
                        >
                            {hero.cta.label}
                        </button>
                    )}
                </div>

                {/* ── restore in flight — the run lives in the main process,
                    so it must be visible (and stoppable) here even after the
                    restore window was closed, not only inside the wizard ── */}
                {restoreActive && (
                    <div className="bks-restore-live">
                        <span className="bks-restore-live-title">Restoring from your backup</span>
                        <span className="bks-restore-live-sub">
                            {restoreStopping ? 'Stopping — finishing the files in flight'
                                : liveRestore?.stage === 'checking'
                                    ? `Checking what’s already here${liveRestore.total ? ` (${(liveRestore.done || 0).toLocaleString()} of ${liveRestore.total.toLocaleString()})` : ''}`
                                    : liveRestore?.total
                                        ? `Downloading and verifying (${Math.min((liveRestore.done || 0) + 1, liveRestore.total)} of ${liveRestore.total})`
                                        : 'Working…'}
                        </span>
                        {/* The set of files moving right now, oldest first —
                            same treatment as the upload hero. Naming only
                            whichever stream ticked last made one line dance
                            between four downloads. */}
                        {!restoreStopping && liveRestore?.inFlightFiles?.length > 0 && (
                            <ul className="bks-hero-files">
                                {liveRestore.inFlightFiles.slice(0, 4).map((f, i) => (
                                    <li key={`${f.name}-${i}`} className="bks-hero-file" title={f.name}>
                                        <span className="bks-hero-file-dot" />
                                        <span className="bks-hero-file-name">{f.name}</span>
                                    </li>
                                ))}
                            </ul>
                        )}
                        {/* One footer row, one baseline: bar, percent, and the
                            actions that govern the run. Buttons hung outside
                            the text column sat at their own heights and
                            squeezed the bar (see MG-1015 screenshots). */}
                        <div className="bks-restore-live-foot">
                            {(() => {
                                // The FILL is fractional, bytes-driven: flooring
                                // to whole percent froze the bar between file
                                // completions and made each finish read as a
                                // jump. The label stays an integer. The checking
                                // pass gets NO fill — its done/total counts
                                // verified files, and drawing them as a bar that
                                // then resets for the downloads reads as the
                                // restore losing progress.
                                const ratio = liveRestore?.stage === 'checking'
                                    ? null
                                    : liveRestore?.bytesTotal
                                        ? Math.min(100, ((liveRestore.bytesReceived || 0) / liveRestore.bytesTotal) * 100)
                                        : null;
                                const pct = liveRestore?.stage === 'checking'
                                    ? null
                                    : ratio != null
                                        ? Math.floor(ratio)
                                        : completionPct(liveRestore?.done, liveRestore?.total);
                                return pct != null ? (
                                    <>
                                        <div className="bks-progress-track">
                                            <div className="bks-progress-fill" style={{ width: `${ratio != null ? ratio.toFixed(2) : pct}%` }} />
                                        </div>
                                        <span className="bks-progress-pct">{pct}%</span>
                                    </>
                                ) : <div className="bks-progress-track" />;
                            })()}
                            {/* No "Details" link: the wizard shows LESS than
                                this card during a run, and the result screen
                                is reachable through Restore… once it ends. */}
                            <button
                                type="button"
                                className="settings-btn"
                                onClick={stopRestore}
                                disabled={restoreStopping}
                            >
                                {restoreStopping ? 'Stopping…' : 'Stop'}
                            </button>
                        </div>
                    </div>
                )}

                {/* ── (6) failure review, expanded from the attention hero ── */}
                {issuesOpen && heroState === 'attention' && (
                    <div className="bks-issues">
                        {/* DJ-data failures come from the polled status, not
                            the files-issues fetch, so they are here the moment
                            the panel opens. Without this section a failed
                            Rekordbox snapshot opened an empty "Nothing left to
                            review" under a hero that had just named it. The
                            raw engine error rides in the tooltip — the row
                            says what to do, the tooltip says what happened. */}
                        {snapshotIssues.length > 0 && (
                            <>
                                <div className="bks-issues-subhead">DJ app data</div>
                                <div className="bks-issues-list">
                                    {snapshotIssues.map((s) => (
                                        <div key={s.tool} className="bks-issue-row">
                                            <span className="bks-issue-name">{s.label}</span>
                                            <span className="bks-issue-reason" title={s.error && s.error !== s.reason ? `${s.reason}\n${s.error}` : s.reason}>{s.reason}</span>
                                        </div>
                                    ))}
                                </div>
                            </>
                        )}
                        {!issues && snapshotIssues.length === 0 && <div className="bks-issues-empty">Loading…</div>}
                        {issues && failedIssues.length === 0 && quotaIssues.length === 0 && snapshotIssues.length === 0 && (
                            <div className="bks-issues-empty">Nothing left to review.</div>
                        )}
                        {failedIssues.length > 0 && (
                            <>
                                {snapshotIssues.length > 0 && <div className="bks-issues-subhead">Music files</div>}
                                <div className="bks-issues-list">
                                    {failedIssues.map((f) => (
                                        <div key={f.filePath} className="bks-issue-row">
                                            <span className="bks-issue-name" title={f.filePath}>{middleEllipsis(f.fileName)}</span>
                                            <span className="bks-issue-reason" title={f.error && f.error !== describeBackupError(f.error) ? `${describeBackupError(f.error)}\n${f.error}` : describeBackupError(f.error)}>{describeBackupError(f.error)}</span>
                                        </div>
                                    ))}
                                </div>
                            </>
                        )}
                        {quotaIssues.length > 0 && (
                            <>
                                <div className="bks-issues-subhead">Skipped — out of space</div>
                                <div className="bks-issues-list">
                                    {quotaIssues.map((f) => (
                                        <div key={f.filePath} className="bks-issue-row">
                                            <span className="bks-issue-name" title={f.filePath}>{middleEllipsis(f.fileName)}</span>
                                            <span className="bks-issue-reason">Not enough space — frees up when you make space</span>
                                        </div>
                                    ))}
                                </div>
                            </>
                        )}
                        <div className="bks-issues-foot">
                            <span className="bks-issues-count">
                                {[
                                    snapshotIssues.length > 0 ? `${snapshotIssues.length} DJ ${snapshotIssues.length === 1 ? 'app' : 'apps'} failed` : null,
                                    (issueCounts?.failed || 0) > 0 ? `${(issueCounts.failed).toLocaleString()} ${issueCounts.failed === 1 ? 'file' : 'files'} failed` : null,
                                    (issueCounts?.skipped_quota || 0) > 0 ? `${(issueCounts.skipped_quota).toLocaleString()} skipped` : null,
                                ].filter(Boolean).join(' · ')}
                            </span>
                            <button
                                type="button"
                                className="bks-link"
                                disabled={retrying || backingUp || backupStatus?.snapshotRunning}
                                onClick={retryAll}
                            >
                                {retrying ? 'Retrying…' : 'Retry all'}
                            </button>
                        </div>
                    </div>
                )}

                {/* Sits between the progress bar and the layer rows: it is an
                    aside about the toast, not part of the live progress it was
                    previously wedged above. */}
                {/* Closing the toast used to be a one-way door for the
                    whole run — hours, on a real library. This is the way
                    back.
                    
                    Deliberately NOT gated on a run being live: the
                    engine is idle between runs, and gating on that meant
                    someone who hid the toast and then looked for it
                    found neither the toast nor any way to get it back.
                    If it is hidden and the layer is on, offer it. */}
                {toastHidden && settings?.files === true && !ceremony && (
                    <div className="bks-toast-restore">
                        <button
                            type="button"
                            className="bks-link"
                            onClick={() => {
                                restoreBackupToast();
                                track('backup_toast_restored', { from: 'backup_page' });
                            }}
                        >
                            Show progress pop-up
                        </button>
                    </div>
                )}
                {/* One row per layer, each with its OWN state. The flat list of
                    green ticks this replaces claimed everything was protected
                    the moment backup was switched on — identical for a DJ whose
                    cues had never been snapshotted and someone fully done.
                    DJ data leads: it finishes in seconds and it is the part
                    that cannot be re-downloaded. */}
                {layerRows.length > 0 && (
                    <div className="bks-layer-rows">
                        {layerRows.map((row) => (
                            <span key={row.key} className={`bks-layer-row bks-layer-row--${row.state}`}>
                                <span className="bks-layer-mark" aria-hidden="true">
                                    {row.state === 'done' ? <SmallCheckIcon /> : <span className="bks-layer-dot" />}
                                </span>
                                <span className="bks-layer-label">{row.label}</span>
                                {row.detail && <span className="bks-layer-detail">{row.detail}</span>}
                                <span className="bks-layer-state">
                                    {row.state === 'done' ? 'Backed up'
                                        : row.state === 'syncing' ? (row.syncingLabel || 'Backing up…')
                                            : row.state === 'attention' ? 'Needs attention'
                                                : row.state === 'off' ? 'Off'
                                                    : row.state === 'none' ? 'Nothing to back up'
                                                        : 'Waiting'}
                                </span>
                            </span>
                        ))}
                    </div>
                )}

                {/* Music files (tens of GB) and DJ app data (a few MB) are not
                    comparable quantities — as proportional segments of one bar
                    the DJ share was sub-pixel forever and the bar taught
                    nothing. Coverage leads; bytes are a quiet second line that
                    only speaks up near the quota; DJ data is its own item. */}
                {settings?.enabled && (coverage || storage) && (
                    <div className="bks-storage">
                        {coverage && (
                            <>
                                <div className="bks-storage-bar">
                                    <div className="bks-storage-seg bks-storage-seg--music" data-nonzero={coverage.stored > 0 ? 'true' : 'false'} style={{ width: `${coverage.pct}%` }} />
                                </div>
                                <div className="bks-storage-legend">
                                    <span className="bks-legend-item">
                                        {coverage.stored.toLocaleString()} of {coverage.total.toLocaleString()} tracks protected
                                    </span>
                                    {storage?.djBytes > 0 && (
                                        <span className="bks-legend-item">
                                            <span className="bks-legend-dot bks-legend-dot--dj" />
                                            DJ app data · {formatBytes(storage.djBytes)}
                                        </span>
                                    )}
                                    {storage?.quota > 0 && (
                                        <span className="bks-storage-total">
                                            {storage.nearlyFull
                                                ? `${formatBytes(storage.stored)} of ${formatBytes(storage.quota)} used`
                                                : `${formatBytes(storage.stored)} stored`}
                                        </span>
                                    )}
                                </div>
                            </>
                        )}
                        {!coverage && storage?.quota > 0 && (
                            <div className="bks-storage-legend">
                                <span className="bks-storage-total">
                                    {formatBytes(storage.stored)} of {formatBytes(storage.quota)} used
                                </span>
                            </div>
                        )}
                        {storage?.pending > 0 && (
                            <div className="bks-storage-pending">
                                {formatBytes(storage.pending)} waiting to upload
                            </div>
                        )}
                        {/* Trials back up everything, so a trial user needs to
                            know — while it still matters — what happens to it.
                            Saying this now is the honest version of the
                            deletion notice, not a surprise 30 days later. */}
                        {isTrial && (
                            <div className="bks-storage-pending">
                                {trialDaysRemaining > 0
                                    ? `Your trial has ${trialDaysRemaining} day${trialDaysRemaining === 1 ? '' : 's'} left. `
                                    : 'Your trial is ending. '}
                                Backups stop when it does, and what&apos;s stored is deleted 30 days later unless you upgrade.
                            </div>
                        )}
                    </div>
                )}
            </div>

            {/* ── (3) per-app cards ── */}
            {settings?.enabled && (
                <div className="bks-app-grid">
                    {BACKUP_TOOLS.map(({ tool, label }) => {
                        const detected = !!backupStatus?.detected?.[tool];
                        const last = settings.lastSnapshot?.[tool];
                        const toolOn = settings.tools?.[tool] !== false;
                        // A per-tool switch only means anything while the DJ
                        // data LAYER is on. With the layer off these rendered
                        // checked-and-blue (just `disabled`, which reads as
                        // nothing at a glance), so all four tools looked
                        // enabled while the panel above correctly said "Off" —
                        // the app appeared to contradict itself. Show the
                        // EFFECTIVE state: layer off means these are off.
                        const layerOn = settings.djData !== false;
                        const effectivelyOn = toolOn && layerOn;
                        return (
                            <div
                                key={tool}
                                className={[
                                    'bks-app-card',
                                    // Two different reasons to look inactive, and
                                    // they mean different things: this app isn't
                                    // installed (nothing you can do) vs the whole
                                    // layer is switched off (one click away).
                                    detected ? '' : 'bks-app-card--undetected',
                                    layerOn ? '' : 'bks-app-card--dimmed',
                                ].filter(Boolean).join(' ')}
                            >
                                <div className="bks-app-head">
                                    <img className="bks-app-glyph" src={TOOL_GLYPHS[tool]} alt="" />
                                    <span className="bks-app-name">{label}</span>
                                    <div className="bks-app-toggle">
                                        <ToggleSwitch
                                            id={`settings-backup-${tool}`}
                                            checked={effectivelyOn}
                                            disabled={!detected}
                                            // Flipping a tool on while the layer
                                            // is off turns the layer on too —
                                            // otherwise the click appears to do
                                            // nothing, which is how people end
                                            // up believing DJ backup is running
                                            // when it is not.
                                            //
                                            // Every OTHER tool has to be named
                                            // explicitly in that patch. `tools`
                                            // defaults to all-true and patches
                                            // merge shallowly, so enabling the
                                            // layer alone silently switched on
                                            // all four — one click on Rekordbox
                                            // turned on Serato, Traktor and
                                            // Virtual DJ as well.
                                            onChange={() => applyBackupPatch(
                                                layerOn
                                                    ? { tools: { [tool]: !toolOn } }
                                                    : {
                                                        djData: true,
                                                        tools: Object.fromEntries(
                                                            BACKUP_TOOLS.map(({ tool: t }) => [t, t === tool])
                                                        ),
                                                    },
                                                !effectivelyOn ? tool : null
                                            )}
                                            label=""
                                        />
                                    </div>
                                </div>
                                <div className="bks-app-status">
                                    {!layerOn ? 'DJ app data backup is off'
                                        : backupToolStatusLine(tool, detected, last)}
                                </div>
                                <div className="bks-app-foot">
                                    <span className="bks-app-size">
                                        {last?.totalBytes ? formatBytes(last.totalBytes) : ''}
                                    </span>
                                    {last && (
                                        <button type="button" className="bks-link" onClick={() => openRestoreFor(tool)}>
                                            Restore
                                        </button>
                                    )}
                                </div>
                            </div>
                        );
                    })}
                </div>
            )}

            {/* ── Config card: master + layer toggles, restore/backup footer ── */}
            {!backupOff && (
            <div className="bks-card bks-config">
                <div className="bks-config-row">
                    <ToggleSwitch
                        id="settings-backup-enabled"
                        checked={!!settings?.enabled}
                        disabled={!isAuthenticated}
                        onChange={onMasterToggle}
                        label={(
                            <span className="bks-row-label">
                                <span className="bks-row-title">Back up automatically</span>
                                <span className="bks-row-sub">
                                    {!isAuthenticated
                                        ? 'Sign in to back up your music library'
                                        : (settings?.enabled
                                            ? 'Your library backs up continuously in the background'
                                            : 'Your music files — safe even if this machine dies')}
                                </span>
                            </span>
                        )}
                    />
                </div>
                {settings?.enabled && (
                    <div className="bks-config-row bks-config-row--sub">
                        <ToggleSwitch
                            id="settings-backup-djdata"
                            checked={settings.djData !== false}
                            onChange={() => applyBackupPatch(
                                { djData: settings.djData === false },
                                settings.djData === false ? 'dj_data' : null
                            )}
                            label={(
                                <span className="bks-row-label">
                                    <span className="bks-row-title bks-row-title--sub">DJ app data</span>
                                    <span className="bks-row-sub">Cues, loops, beat grids &amp; playlists</span>
                                </span>
                            )}
                        />
                    </div>
                )}
                {settings?.enabled && (
                    <div className="bks-config-row bks-config-row--sub">
                        <ToggleSwitch
                            id="settings-backup-files"
                            checked={settings.files === true}
                            onChange={onFilesToggle}
                            label={(
                                <span className="bks-row-label">
                                    <span className="bks-row-title bks-row-title--sub">Music files</span>
                                    <span className="bks-row-sub">{backupFilesStatusLine(backupStatus)}</span>
                                </span>
                            )}
                        />
                    </div>
                )}
                {settings?.enabled && settings.files === true && (
                    <div className="bks-config-row bks-config-row--sub">
                        <ToggleSwitch
                            id="settings-backup-metered"
                            checked={settings.pauseOnMetered !== false}
                            onChange={() => applyBackupPatch({ pauseOnMetered: settings.pauseOnMetered === false })}
                            label={(
                                <span className="bks-row-label">
                                    <span className="bks-row-title bks-row-title--sub">Pause on metered connections</span>
                                    <span className="bks-row-sub">
                                        Holds off on cellular and connections your system marks as metered
                                    </span>
                                </span>
                            )}
                        />
                    </div>
                )}
                {settings?.enabled && settings.files === true && (
                    <div className="bks-config-row bks-config-row--sub bks-config-row--stack">
                        <span className="bks-row-label">
                            <span className="bks-row-title bks-row-title--sub">Upload speed</span>
                            <span className="bks-row-sub">
                                Slow it down to keep the rest of your connection free while a big first backup runs
                            </span>
                        </span>
                        <div className="bks-speed" role="radiogroup" aria-label="Upload speed">
                            {BACKUP_SPEED_OPTIONS.map((o) => (
                                <button
                                    key={o.key}
                                    type="button"
                                    role="radio"
                                    aria-checked={(settings.uploadSpeed || 'full') === o.key}
                                    className={`bks-speed-opt${(settings.uploadSpeed || 'full') === o.key ? ' bks-speed-opt--on' : ''}`}
                                    onClick={() => {
                                        if ((settings.uploadSpeed || 'full') === o.key) return;
                                        applyBackupPatch({ uploadSpeed: o.key });
                                        track('backup_speed_changed', { speed: o.key });
                                    }}
                                    title={o.hint}
                                >
                                    {o.label}
                                </button>
                            ))}
                        </div>
                    </div>
                )}
                <div className="bks-config-footer">
                    {/* Demoted from a primary "Back up now": the system
                        detects changes itself (event triggers + the DJ-db
                        watcher + daily reconcile), so a big button implying
                        routine manual work is the wrong model — Dropbox has
                        no "sync now". What remains is the certainty lever
                        for the about-to-wipe-this-laptop moment: force the
                        check right now. Files stay stopped if the user
                        stopped them (see backUpNow). */}
                    <button
                        type="button"
                        className="bks-link bks-check-now"
                        onClick={backUpNow}
                        disabled={!isAuthenticated || backingUp || backupStatus?.snapshotRunning || heroState === 'backing_up'}
                    >
                        {(backingUp || backupStatus?.snapshotRunning || heroState === 'backing_up') ? 'Checking…' : 'Check now'}
                    </button>
                    <div className="bks-config-footer-spacer" />
                    <button
                        type="button"
                        className="settings-btn"
                        onClick={() => { setRestoreTool(null); setShowRestore(true); }}
                        disabled={!isAuthenticated}
                    >
                        {restoreActive ? 'Restore in progress…' : 'Restore…'}
                    </button>
                </div>
            </div>
            )}

            {deleteResult && (
                <p className="bks-footnote bks-footnote--confirm">
                    {`Cloud Backup deleted — ${deleteResult.files.toLocaleString()} file${deleteResult.files === 1 ? '' : 's'}`}
                    {deleteResult.snapshots > 0
                        ? ` and ${deleteResult.snapshots} DJ app snapshot${deleteResult.snapshots === 1 ? '' : 's'}`
                        : ''}
                    {'. Removal finishes on our servers, so it carries on even if you close the app.'}
                </p>
            )}
            {snapshotResults && (
                <p className="bks-footnote">
                    {snapshotResults
                        .filter((r) => r.tool !== 'model' || r.status === 'failed')
                        .map((r) => {
                            const label = BACKUP_TOOLS.find((t) => t.tool === r.tool)?.label || 'Portable copy';
                            if (r.status === 'stored') return `${label}: backed up (${formatBytes(r.totalBytes)})`;
                            if (r.status === 'unchanged') return `${label}: already up to date`;
                            if (r.status === 'skipped_running') return `${label}: skipped — app is open`;
                            if (r.status === 'not_detected') return `${label}: not detected`;
                            return `${label}: failed (${r.error || 'unknown error'})`;
                        })
                        .join(' · ')}
                </p>
            )}
            <p className="bks-footnote">
                Snapshots your DJ apps&apos; databases to MixGoat Cloud Backup whenever they change — after a
                sync, and when a DJ app saves edits you made in it directly. Only real changes upload. A
                portable copy is included so you can restore into a different DJ app. Music files, when on,
                upload in the background as your library changes.
            </p>

            {/* ── (7) danger zone: delete everything in the cloud ── */}
            {isAuthenticated && (
                <div className="bks-danger">
                    <div className="bks-danger-text">
                        <span className="bks-danger-title">Delete cloud backup</span>
                        <span className="bks-danger-sub">
                            Permanently removes every backed-up file and DJ app snapshot from MixGoat&apos;s
                            servers. Your local library is untouched. This can&apos;t be undone.
                        </span>
                    </div>
                    <button
                        type="button"
                        className="settings-btn bks-danger-btn"
                        onClick={() => { setDeleteOpen(true); setDeleteConfirmText(''); setDeleteError(null); track('backup_delete_opened'); }}
                    >
                        Delete…
                    </button>
                </div>
            )}

            {deleteOpen && (
                <div className="backup-restore-overlay" onMouseDown={(e) => { if (e.target === e.currentTarget && !deleting) setDeleteOpen(false); }}>
                    <div className="backup-restore-modal bks-preflight" role="dialog" aria-label="Delete cloud backup">
                        <div className="brm-header">
                            <span className="brm-title">Delete cloud backup</span>
                            <button type="button" className="brm-close" onClick={() => setDeleteOpen(false)} disabled={deleting} aria-label="Close">×</button>
                        </div>
                        <div className="brm-body">
                            <p className="brm-lead">
                                This deletes everything MixGoat holds for you: {usage?.fileCount ? `${usage.fileCount.toLocaleString()} ${usage.fileCount === 1 ? 'file' : 'files'}` : 'all backed-up files'}
                                {storage?.djBytes > 0 ? ' and every DJ app snapshot' : ''}
                                {storage?.stored > 0 ? ` (${formatBytes(storage.stored)})` : ''}.
                            </p>
                            <p className="brm-muted">
                                Your music and DJ apps on this computer are not touched. Cloud Backup will be
                                turned off — you can turn it back on any time and start fresh.
                            </p>
                            <label className="bks-danger-label" htmlFor="bks-delete-confirm">
                                Type DELETE to confirm
                            </label>
                            <input
                                id="bks-delete-confirm"
                                className="bks-danger-input"
                                type="text"
                                autoComplete="off"
                                spellCheck={false}
                                value={deleteConfirmText}
                                disabled={deleting}
                                onChange={(e) => setDeleteConfirmText(e.target.value)}
                            />
                            {deleteError && <p className="bks-danger-error">{deleteError}</p>}
                        </div>
                        <div className="brm-footer">
                            <button type="button" className="brm-secondary-btn" onClick={() => setDeleteOpen(false)} disabled={deleting}>Cancel</button>
                            <div className="brm-footer-spacer" />
                            <button
                                type="button"
                                className="brm-primary-btn bks-danger-confirm"
                                disabled={deleting || deleteConfirmText.trim().toUpperCase() !== 'DELETE'}
                                onClick={confirmDelete}
                            >
                                {deleting ? 'Deleting…' : 'Delete everything'}
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* ── (5) music-files pre-flight sheet ── */}
            {preflight && (
                <div className="backup-restore-overlay" onMouseDown={(e) => { if (e.target === e.currentTarget) dismissPreflight(); }}>
                    <div className="backup-restore-modal bks-preflight" role="dialog" aria-label={preflight.mode === 'enable' ? 'What should we back up?' : 'Back up your music files'}>
                        <div className="brm-header">
                            <span className="brm-title">
                                {preflight.mode === 'enable' ? 'What should we back up?' : 'Back up your music files'}
                            </span>
                            <button type="button" className="brm-close" onClick={dismissPreflight} aria-label="Close">×</button>
                        </div>
                        <div className="brm-body">
                            {preflight.mode === 'enable' ? (
                                <div className="bks-pick-list">
                                    <label className="bks-pick" htmlFor="bks-pick-files">
                                        <input
                                            id="bks-pick-files"
                                            type="checkbox"
                                            checked={!!preflight.pickFiles}
                                            onChange={() => setPreflight((c) => ({ ...c, pickFiles: !c.pickFiles }))}
                                        />
                                        <span className="bks-pick-text">
                                            <span className="bks-pick-title">Music files</span>
                                            <span className="bks-pick-sub">
                                                {preflight.loading
                                                    ? 'Sizing up your library…'
                                                    : (backupPreflightSummary(preflight)
                                                        || 'Files you add will back up automatically')}
                                            </span>
                                        </span>
                                    </label>
                                    {/* Name the apps actually found, and don't
                                        offer the layer at all when there are
                                        none: ticking it would enable something
                                        that can never produce a snapshot. With
                                        one app the layer tick IS the app tick;
                                        from two up, each app gets its own so
                                        the promise and the choice are the same
                                        thing. */}
                                    <label
                                        className={`bks-pick${detectedTools.length ? '' : ' bks-pick--unavailable'}`}
                                        htmlFor="bks-pick-dj"
                                    >
                                        <input
                                            id="bks-pick-dj"
                                            type="checkbox"
                                            checked={detectedTools.length ? !!preflight.pickDj : false}
                                            disabled={!detectedTools.length}
                                            onChange={() => setPreflight((c) => {
                                                const on = !c.pickDj;
                                                // Re-ticking the layer after every app was
                                                // unticked re-arms them all — a ticked layer
                                                // with zero ticked apps promises nothing. A
                                                // partial pick survives an off/on round trip.
                                                const allOff = detectedTools.every(({ tool }) => c.pickTools?.[tool] === false);
                                                return { ...c, pickDj: on, pickTools: on && allOff ? {} : c.pickTools };
                                            })}
                                        />
                                        <span className="bks-pick-text">
                                            <span className="bks-pick-title">DJ app data</span>
                                            <span className="bks-pick-sub">
                                                {!detectedTools.length
                                                    ? 'No DJ apps found on this computer'
                                                    : detectedTools.length === 1
                                                        ? `${detectedTools[0].label} — cues, grids and playlists, the part you can’t re-download`
                                                        : 'Cues, grids and playlists — the part you can’t re-download'}
                                            </span>
                                        </span>
                                    </label>
                                    {/* Sibling of the layer label, NOT nested in
                                        it — a checkbox inside that <label> would
                                        toggle the layer on every app click. */}
                                    {detectedTools.length > 1 && (
                                        <div className="bks-pick-apps" role="group" aria-label="DJ apps to back up">
                                            {detectedTools.map(({ tool, label }) => (
                                                <label key={tool} className="bks-pick-app" htmlFor={`bks-pick-app-${tool}`}>
                                                    <input
                                                        id={`bks-pick-app-${tool}`}
                                                        type="checkbox"
                                                        checked={!!preflight.pickDj && preflight.pickTools?.[tool] !== false}
                                                        onChange={() => setPreflight((c) => {
                                                            // With the layer off every app shows
                                                            // unticked, so a click there means "just
                                                            // this one": turn the layer on and name the
                                                            // rest false explicitly (same rule as the
                                                            // per-app cards), or they'd all spring back
                                                            // ticked with it.
                                                            if (!c.pickDj) {
                                                                return {
                                                                    ...c,
                                                                    pickDj: true,
                                                                    pickTools: Object.fromEntries(detectedTools.map(({ tool: t }) => [t, t === tool])),
                                                                };
                                                            }
                                                            const next = { ...c.pickTools, [tool]: c.pickTools?.[tool] === false };
                                                            // Unticking the last app is unticking the layer.
                                                            const any = detectedTools.some(({ tool: t }) => next[t] !== false);
                                                            return { ...c, pickTools: next, pickDj: any };
                                                        })}
                                                    />
                                                    {label}
                                                </label>
                                            ))}
                                        </div>
                                    )}
                                </div>
                            ) : (
                                <>
                                    {preflight.loading ? (
                                        <p className="brm-muted">Sizing up your library…</p>
                                    ) : preflight.count > 0 ? (
                                        <p className="brm-lead bks-preflight-summary">
                                            {backupPreflightSummary(preflight)}
                                        </p>
                                    ) : (
                                        <p className="brm-lead bks-preflight-summary">
                                            Files you add to your library will back up automatically.
                                        </p>
                                    )}
                                </>
                            )}
                            <p className="brm-muted">
                                Uploads run quietly in the background; you can keep using MixGoat.
                            </p>
                        </div>
                        <div className="brm-footer">
                            <button type="button" className="brm-secondary-btn" onClick={dismissPreflight}>Not now</button>
                            <div className="brm-footer-spacer" />
                            <button
                                type="button"
                                className="brm-primary-btn"
                                disabled={preflight.loading
                                    || (preflight.mode === 'enable' && !preflight.pickFiles && !preflight.pickDj)}
                                onClick={confirmPreflight}
                            >
                                {/* When the queue says every file is already
                                    up there, "Start backing up" promises an
                                    upload that won't happen — the click just
                                    flips the switch, so say that. */}
                                {backupPreviewAllStored(preflight) ? 'Turn on backup' : 'Start backing up'}
                            </button>
                        </div>
                    </div>
                </div>
            )}

            <BackupRestoreModal
                show={showRestore}
                initialTool={restoreTool}
                restoreRunningElsewhere={restoreActive}
                onHide={() => { setShowRestore(false); setRestoreTool(null); refreshBackupStatus(); }}
            />
        </div>
    );
}

/**
 * Hero copy per state. Returns { title, sub, pct?, cta? } — cta.onClick is a
 * tag ('enable' | 'upgrade') resolved by the component so this stays data.
 */
function buildHero({ heroState, ceremony, backupStatus, settings, usage, filesProgress, backupProgress, isAuthenticated, cloudFacts, stopRequested }) {
    if (ceremony === 'first') {
        const uploaded = settings?.lastFilesRun?.uploadedCount || 0;
        return {
            title: 'Your DJ life is safe now.',
            sub: uploaded > 0 ? `${uploaded.toLocaleString()} ${uploaded === 1 ? 'file' : 'files'} uploaded and verified.` : 'Backed up.',
        };
    }
    if (ceremony === 'again') return { title: 'Backed up', sub: 'Everything new is safe.' };

    switch (heroState) {
        case 'off': {
            // The switch is per-MACHINE. When the account already has a cloud
            // backup, "Not protected — nothing is backed up" is flatly wrong
            // (the restore dialog two clicks away lists the snapshots) — the
            // honest claim is scoped to this computer, with the cloud copy
            // acknowledged as safe.
            if (isAuthenticated && cloudFacts) {
                const src = [
                    cloudFacts.hostname ? `from ${cloudFacts.hostname}` : null,
                    cloudFacts.lastAt ? formatTimeAgo(cloudFacts.lastAt) : null,
                ].filter(Boolean).join(', ');
                return {
                    title: 'This computer isn’t backing up',
                    sub: `Your cloud backup${src ? ` (${src})` : ''} is safe — nothing here is being added to it.`,
                    cta: { label: 'Turn on backup', onClick: 'enable' },
                };
            }
            return {
                title: 'Not protected',
                sub: isAuthenticated
                    ? 'Your cues, playlists and music aren’t backed up.'
                    : 'Sign in to back up your cues, playlists and music.',
                cta: { label: 'Turn on backup', onClick: 'enable', disabled: !isAuthenticated },
            };
        }
        case 'paused': {
            const countLine = backupFilesCountLine(backupStatus);
            return {
                title: 'Backup stopped',
                sub: countLine ? `${countLine} — resume any time, nothing already uploaded is lost.`
                    : 'Resume any time — nothing already uploaded is lost.',
                cta: { label: 'Resume', onClick: 'resume' },
            };
        }
        case 'snoozed': {
            // Says WHEN it comes back, so the pause never feels like a switch
            // the user has to remember to flip. Resume is still offered for
            // "actually, go ahead now".
            const countLine = backupFilesCountLine(backupStatus);
            const when = backupSnoozeLine(settings) || 'Backups resume shortly';
            return {
                title: 'Paused for now',
                sub: countLine ? `${countLine} · ${when}` : when,
                cta: { label: 'Resume now', onClick: 'resume' },
            };
        }
        case 'waiting_network': {
            const countLine = backupFilesCountLine(backupStatus);
            return {
                title: 'Waiting for a better connection',
                sub: countLine
                    ? `${countLine} · This connection looks metered — backups continue automatically once you're back on an unmetered one.`
                    : 'This connection looks metered — backups continue automatically once you’re back on an unmetered one.',
            };
        }
        case 'paused_entitlement':
            return {
                title: 'Backup paused',
                sub: 'GOAT is required to keep backing up.',
                cta: { label: 'Upgrade', onClick: 'upgrade' },
            };
        case 'backing_up': {
            if (filesProgress) {
                // The headline is the LIBRARY-wide count — "how many of mine
                // are safe" — not the current run's slice of it. The stage and
                // filename go underneath as the detail line, so the hero never
                // sits there count-less through a long hashing pass.
                const countLine = backupProgressLine(filesProgress) || backupFilesCountLine(backupStatus);
                const pct = backupProgressPct(filesProgress);
                const stageLabel = backupStageLabel(filesProgress);
                const inFlightLabel = backupInFlightLabel(filesProgress);
                // Deliberately NO "n of N" here any more. That counter was
                // scoped to the CURRENT RUN, so every time a run ended and the
                // next one picked up the queue it restarted near zero — the
                // line went "45 of 3,453", then "19 of 3,453" minutes later,
                // which reads as the backup losing progress. The library-wide
                // count in the title is the one that only ever moves forward;
                // this line says what is happening, not how far along it is.
                const inFlightFiles = filesProgress.inFlightFiles || [];
                const fileName = inFlightFiles.length === 0 && filesProgress.currentFile
                    ? String(filesProgress.currentFile).split('/').pop()
                    : null;
                const failed = (filesProgress.libraryFailed || 0) + (filesProgress.librarySkippedQuota || 0);
                // A NODE, not a string. Truncating the name to a fixed 30
                // characters still overflowed on wide names and wrapped to a
                // second line, so the whole card grew and shrank once per file
                // — a layout jump every couple of seconds. The stage and the
                // failure count are fixed-width; the name gets whatever is
                // left and ellipsises in CSS, so the row is always one line.
                return {
                    title: countLine || 'Backing up…',
                    sub: (
                        <span className="bks-hero-sub-progress">
                            <span className="bks-hero-sub-stage">{stageLabel}</span>
                            {fileName && (
                                <span className="bks-hero-sub-file" title={filesProgress.currentFile}>
                                    {fileName}
                                </span>
                            )}
                            {inFlightLabel && (
                                <span className="bks-hero-sub-inflight">{inFlightLabel}</span>
                            )}
                            {failed > 0 && (
                                <span className="bks-hero-sub-failed">
                                    {`${failed.toLocaleString()} failed`}
                                </span>
                            )}
                        </span>
                    ),
                    pct,
                    inFlightFiles,
                };
            }
            if (backupProgress) {
                const label = BACKUP_TOOLS.find((t) => t.tool === backupProgress.tool)?.label
                    || (backupProgress.tool === 'model' ? 'Portable copy' : backupProgress.tool);
                // Stop was pressed while the snapshot pass runs. The pass has
                // no abort path (seconds per tool) but the files leg is
                // already paused — saying "Backing up…" here reads as the
                // click having done nothing.
                if (stopRequested) {
                    return { title: 'Stopping…', sub: 'Finishing the DJ-data check — nothing more will upload.' };
                }
                if (backupProgress.phase === 'uploading' && backupProgress.totalBytes) {
                    const pct = completionPct(backupProgress.bytesSent, backupProgress.totalBytes);
                    return { title: 'Backing up…', sub: `Uploading ${label}`, pct };
                }
                // Until something uploads this is a change CHECK — export the
                // DJ databases, hash, compare with the last stored snapshot,
                // upload only on drift. "Backing up…" over an unchanged
                // library claims a re-upload that is not happening.
                return { title: 'Backing up…', sub: `Checking ${label} for changes` };
            }
            return { title: 'Backing up…', sub: 'Uploading in the background.' };
        }
        case 'attention': {
            const counts = backupStatus?.files?.counts;
            const parts = [];
            if (counts?.failed > 0) parts.push(`${counts.failed.toLocaleString()} ${counts.failed === 1 ? 'file' : 'files'} couldn’t upload`);
            if (counts?.skipped_quota > 0) parts.push(`${counts.skipped_quota.toLocaleString()} skipped for space`);
            // Every failed app, not the first: with two down the second was
            // invisible until the first was fixed.
            const failedTools = backupSnapshotIssues(backupStatus).map((s) => s.label);
            if (failedTools.length) {
                parts.push(`${joinNames(failedTools)} backup${failedTools.length > 1 ? 's' : ''} failed`);
            }
            // Run-level files failure (negotiate/network) — no per-file rows,
            // but the queue is stalled on it. Retries run automatically.
            if (!parts.length && settings?.lastFilesRun?.error) {
                parts.push(`File backup hit a problem (${settings.lastFilesRun.error}) — retrying automatically`);
            }
            return { title: 'Needs attention', sub: parts.join(' · ') || 'Some items didn’t back up.' };
        }
        case 'queued': {
            // Rows outstanding, engine NOT running. This used to read "Backing
            // up your library — uploads continue in the background", which was
            // simply false: nothing was continuing, and the count sat frozen at
            // whatever the last run reached. Say what is true and offer the
            // button that fixes it.
            const countLine = backupFilesCountLine(backupStatus);
            const counts = backupStatus?.files?.counts;
            const waiting = counts
                ? (counts.pending || 0) + (counts.hashing || 0) + (counts.ready || 0) + (counts.uploading || 0)
                : 0;
            return {
                title: 'Backup is paused between runs',
                sub: [
                    countLine,
                    waiting ? `${waiting.toLocaleString()} still to upload` : null,
                ].filter(Boolean).join(' · ') || 'Some tracks are still waiting to upload.',
                cta: { label: 'Back up now', onClick: 'resume' },
            };
        }
        case 'frozen': {
            // The entitlement lapsed. Everything already uploaded is intact and
            // downloadable; nothing new goes up; there is a deletion date.
            const usageNow = backupStatus?.usage;
            const days = usageNow?.daysUntilPurge;
            const countLine = backupFilesCountLine(backupStatus)
                || (usageNow?.fileCount ? `${usageNow.fileCount.toLocaleString()} files backed up` : null);
            const deadline = days == null
                ? 'Renew GOAT to start protecting it again.'
                : days <= 0
                    ? 'It is scheduled for deletion — renew GOAT now to keep it.'
                    : `It will be deleted in ${days} day${days === 1 ? '' : 's'} unless you renew GOAT.`;
            return {
                title: 'Your backup is frozen',
                sub: [countLine, `${deadline} Downloads keep working either way.`]
                    .filter(Boolean).join(' · '),
                cta: { label: 'Renew GOAT', onClick: 'upgrade' },
            };
        }
        default: {
            // protected
            const parts = [];
            if (settings?.files === true) {
                // "1,234 of 1,300 tracks backed up" from the local queue; the
                // server-side fileCount is the fallback when counts are absent.
                const countLine = backupFilesCountLine(backupStatus);
                if (countLine) parts.push(countLine);
                else if (usage?.fileCount > 0) parts.push(`${usage.fileCount.toLocaleString()} ${usage.fileCount === 1 ? 'file' : 'files'}`);
            }
            const appCount = BACKUP_TOOLS.filter(({ tool }) => settings?.tools?.[tool] !== false
                && backupStatus?.detected?.[tool]
                && settings?.lastSnapshot?.[tool]).length;
            if (appCount > 0) parts.push(`${appCount} DJ ${appCount === 1 ? 'app' : 'apps'}`);
            const newest = Math.max(
                0,
                ...Object.values(settings?.lastSnapshot || {}).map((l) => l?.at || 0),
                settings?.lastFilesRun?.at || 0
            );
            if (newest) parts.push(formatTimeAgo(newest));
            if (parts.length === 0) {
                // Not "runs after your next sync": enabling a layer kicks a run
                // immediately (backup:setEnabled → runBackupFiles), so that copy
                // sent people looking for an extra step that does not exist.
                const nothingOn = settings?.files !== true && settings?.djData === false;
                if (nothingOn) {
                    return {
                        title: 'Nothing selected to back up',
                        sub: 'Choose music files, DJ app data, or both — nothing is being backed up yet.',
                    };
                }
                return { title: 'Backup is on', sub: 'Starting your first backup…' };
            }
            return { title: 'All backed up', sub: parts.join(' · ') };
        }
    }
}

export default BackupSection;
